import React, { useState } from 'react';
import { 
  X, 
  FileText, 
  DollarSign, 
  Calendar,
  CheckCircle,
  XCircle,
  Download,
  Loader2
} from 'lucide-react';
import exportPaymentTransactionsPDF from './PaymentTransactionExport';
import AttachmentUpload from './AttachmentUpload';
import './AddDeal.css';

const DealView = ({ isOpen, onClose, deal = null, onEdit, onExportPurchaseDO, onExportSellDO, showroomDetails }) => {
  const [exportingPurchasePDF, setExportingPurchasePDF] = useState(false);
  const [exportingSalePDF, setExportingSalePDF] = useState(false);
  const [exportingPurchaseDO, setExportingPurchaseDO] = useState(false);
  const [exportingSellDO, setExportingSellDO] = useState(false);

  if (!isOpen || !deal) return null;

  const getCarDisplayName = (car) => {
    if (!car) return 'N/A';
    const reg = car.registrationNumber || '';
    const chassis = car.chassisNumber || '';
    const engine = car.engineNumber || '';
    return `${car.name} ${car.vehicleModel} ${reg ? `(${reg})` : chassis ? `(${chassis})` : engine ? `(${engine})` : ''}`.trim();
  };

  const getCustomerDisplayName = (customer) => {
    if (!customer) return 'N/A';
    const identifier = customer.cnic || customer.phoneNumber || 'N/A';
    return `${customer.fullName} (${identifier})`;
  };

  // Helper to safely convert string to number for calculations
  const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'string') {
      const num = parseFloat(value);
      return isNaN(num) ? 0 : num;
    }
    return typeof value === 'number' ? value : 0;
  };

  const formatCurrency = (amount) => {
    // Handle both string and number inputs
    const numAmount = toNumber(amount);
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR'
    }).format(numAmount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-PK');
  };

  const calculatePaymentSummary = (payments) => {
    const total = payments.reduce((sum, payment) => sum + toNumber(payment.amount), 0);
    const received = payments.filter(payment => payment.status === 'RECEIVED')
      .reduce((sum, payment) => sum + toNumber(payment.amount), 0);
    const pending = total - received;
    
    return { total, received, pending };
  };

  const calculateFinancialSummary = () => {
    const purchaseValue = toNumber(deal.purchaseInfo?.value);
    const sellValue = toNumber(deal.sellInfo?.value);
    const expenses = toNumber(deal.expenses);
    
    const grossProfit = sellValue - purchaseValue;
    const netProfit = grossProfit - expenses;
    const profitMargin = sellValue > 0 ? (netProfit / sellValue) * 100 : 0;

    return {
      purchaseValue,
      sellValue,
      expenses,
      grossProfit,
      netProfit,
      profitMargin: profitMargin.toFixed(2)
    };
  };

  const financialSummary = calculateFinancialSummary();
  const purchasePaymentSummary = calculatePaymentSummary(deal.purchasePaymentStructures || []);
  const sellPaymentSummary = calculatePaymentSummary(deal.sellPaymentStructures || []);

  const handleExportPayments = async (type) => {
    if (type === 'purchase') {
      setExportingPurchasePDF(true);
    } else {
      setExportingSalePDF(true);
    }
    try {
      await exportPaymentTransactionsPDF(deal, type, showroomDetails, (loading) => {
        if (type === 'purchase') {
          setExportingPurchasePDF(loading);
        } else {
          setExportingSalePDF(loading);
        }
      });
    } catch (error) {
      console.error('Failed to export payment transactions PDF:', error);
      alert('Failed to export payment transactions PDF');
      setExportingPurchasePDF(false);
      setExportingSalePDF(false);
    }
  };

  const handleExportPurchaseDO = async () => {
    if (!onExportPurchaseDO) return;
    setExportingPurchaseDO(true);
    try {
      await onExportPurchaseDO(deal.id);
    } catch (error) {
      console.error('Failed to export purchase DO:', error);
    } finally {
      setExportingPurchaseDO(false);
    }
  };

  const handleExportSellDO = async () => {
    if (!onExportSellDO) return;
    setExportingSellDO(true);
    try {
      await onExportSellDO(deal.id);
    } catch (error) {
      console.error('Failed to export sell DO:', error);
    } finally {
      setExportingSellDO(false);
    }
  };

  return (
    <div className="add-deal-modal-overlay">
      <div className="add-deal-modal">
        <div className="modal-header">
          <h2>Deal Details - {deal.dealId}</h2>
          <button onClick={onClose} className="close-btn">
            <X size={20} />
          </button>
        </div>

        <div className="deal-view-content">
          {/* Car Information */}
          <div className="form-section">
            <h3>Car Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Car:</label>
                <span>{getCarDisplayName(deal.car)}</span>
              </div>
              <div className="detail-item">
                <label>Registration:</label>
                <span>{deal.car?.registrationNumber || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Chassis:</label>
                <span>{deal.car?.chassisNumber || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Color:</label>
                <span>{deal.car?.color || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Purchase Information */}
          <div className="form-section">
            <h3>Purchase Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Purchased From:</label>
                <span>{getCustomerDisplayName(deal.purchaseInfo?.customer)}</span>
              </div>
              <div className="detail-item">
                <label>Purchase Value:</label>
                <span>{formatCurrency(deal.purchaseInfo?.value)}</span>
              </div>
              <div className="detail-item">
                <label>Purchase Date:</label>
                <span>{formatDate(deal.purchaseInfo?.dealDate)}</span>
              </div>
              <div className="detail-item">
                <label>Files Received:</label>
                <span className={deal.purchaseInfo?.filesReceived ? 'status-success' : 'status-pending'}>
                  {deal.purchaseInfo?.filesReceived ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="detail-item">
                <label>Number Plates Received:</label>
                <span className={deal.purchaseInfo?.numberPlatesReceived ? 'status-success' : 'status-pending'}>
                  {deal.purchaseInfo?.numberPlatesReceived ? 'Yes' : 'No'}
                </span>
              </div>
            </div>

            {/* Purchase Payment Structures */}
            {deal.purchasePaymentStructures && deal.purchasePaymentStructures.length > 0 && (
              <div className="payments-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h4>Purchase Payment Structures</h4>
                  <button 
                    type="button" 
                    onClick={() => handleExportPayments('purchase')}
                    className="print-btn"
                    style={{ fontSize: '12px', padding: '4px 8px' }}
                    title="Export Purchase Payment Transactions"
                    disabled={exportingPurchasePDF}
                  >
                    {exportingPurchasePDF ? (
                      <>
                        <Loader2 size={14} className="spinning" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <FileText size={14} />
                        Export Purchase PDF
                      </>
                    )}
                  </button>
                </div>
                <div className="payment-summary">
                  <span>Total: {formatCurrency(purchasePaymentSummary.total)}</span>
                  <span>Received: {formatCurrency(purchasePaymentSummary.received)}</span>
                  <span>Pending: {formatCurrency(purchasePaymentSummary.pending)}</span>
                </div>
                
                <div className="payments-list">
                  {deal.purchasePaymentStructures.map((payment, index) => (
                    <div key={payment.id || index} className={`payment-item ${payment.status}`}>
                      <div className="payment-info">
                        <div className="payment-amount">{formatCurrency(payment.amount)}</div>
                        <div className="payment-details">
                          {payment.description} {payment.dueDate && `• Due: ${formatDate(payment.dueDate)}`}
                        </div>
                        {payment.status === 'RECEIVED' && payment.receivedAt && (
                          <div className="payment-received">
                            Received: {formatDate(payment.receivedAt)}
                          </div>
                        )}
                      </div>
                      <div className="payment-status">
                        <span className={`status-badge ${payment.status.toLowerCase()}`}>
                          {payment.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sale Information */}
          {deal.sellInfo && (
            <div className="form-section">
              <h3>Sale Information</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Sold To:</label>
                  <span>{getCustomerDisplayName(deal.sellInfo?.customer)}</span>
                </div>
                <div className="detail-item">
                  <label>Sale Value:</label>
                  <span>{formatCurrency(deal.sellInfo?.value)}</span>
                </div>
                <div className="detail-item">
                  <label>Sale Date:</label>
                  <span>{formatDate(deal.sellInfo?.dealDate)}</span>
                </div>
                <div className="detail-item">
                  <label>Files Received:</label>
                  <span className={deal.sellInfo?.filesReceived ? 'status-success' : 'status-pending'}>
                    {deal.sellInfo?.filesReceived ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Number Plates Received:</label>
                  <span className={deal.sellInfo?.numberPlatesReceived ? 'status-success' : 'status-pending'}>
                    {deal.sellInfo?.numberPlatesReceived ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>

              {/* Sell Payment Structures */}
              {deal.sellPaymentStructures && deal.sellPaymentStructures.length > 0 && (
                <div className="payments-section">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4>Sell Payment Structures</h4>
                    <button 
                      type="button" 
                      onClick={() => handleExportPayments('sale')}
                      className="print-btn"
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                      title="Export Sale Payment Transactions"
                      disabled={exportingSalePDF}
                    >
                      {exportingSalePDF ? (
                        <>
                          <Loader2 size={14} className="spinning" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <FileText size={14} />
                          Export Sale PDF
                        </>
                      )}
                    </button>
                  </div>
                  <div className="payment-summary">
                    <span>Total: {formatCurrency(sellPaymentSummary.total)}</span>
                    <span>Received: {formatCurrency(sellPaymentSummary.received)}</span>
                    <span>Pending: {formatCurrency(sellPaymentSummary.pending)}</span>
                  </div>
                  
                  <div className="payments-list">
                    {deal.sellPaymentStructures.map((payment, index) => (
                      <div key={payment.id || index} className={`payment-item ${payment.status}`}>
                        <div className="payment-info">
                          <div className="payment-amount">{formatCurrency(payment.amount)}</div>
                          <div className="payment-details">
                            {payment.description} {payment.dueDate && `• Due: ${formatDate(payment.dueDate)}`}
                          </div>
                          {payment.status === 'RECEIVED' && payment.receivedAt && (
                            <div className="payment-received">
                              Received: {formatDate(payment.receivedAt)}
                            </div>
                          )}
                        </div>
                        <div className="payment-status">
                          <span className={`status-badge ${payment.status.toLowerCase()}`}>
                            {payment.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Financial Summary */}
          <div className="form-section">
            <h3>Financial Summary</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Purchase Value:</label>
                <span>{formatCurrency(financialSummary.purchaseValue)}</span>
              </div>
              <div className="detail-item">
                <label>Sale Value:</label>
                <span>{formatCurrency(financialSummary.sellValue)}</span>
              </div>
              <div className="detail-item">
                <label>Total Expenses:</label>
                <span>{formatCurrency(financialSummary.expenses)}</span>
              </div>
              <div className="detail-item">
                <label>Gross Profit:</label>
                <span className={financialSummary.grossProfit >= 0 ? 'status-success' : 'status-error'}>
                  {formatCurrency(financialSummary.grossProfit)}
                </span>
              </div>
              <div className="detail-item">
                <label>Net Profit:</label>
                <span className={financialSummary.netProfit >= 0 ? 'status-success' : 'status-error'}>
                  {formatCurrency(financialSummary.netProfit)}
                </span>
              </div>
              <div className="detail-item">
                <label>Profit Margin:</label>
                <span className={financialSummary.profitMargin >= 0 ? 'status-success' : 'status-error'}>
                  {financialSummary.profitMargin}%
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {deal.notes && (
            <div className="form-section">
              <h3>Notes</h3>
              <div className="notes-content">
                {deal.notes}
              </div>
            </div>
          )}

          {/* Attachments */}
          <div className="form-section">
            <h3>Attachments</h3>
            <AttachmentUpload dealId={deal.id} />
          </div>
        </div>

        {/* View Actions */}
        <div className="form-actions">
          <div className="action-buttons">
            <button type="button" onClick={onClose} className="cancel-btn">
              Close
            </button>
            {onEdit && (
              <button type="button" onClick={onEdit} className="submit-btn">
                Edit Deal
              </button>
            )}
          </div>
          
          {/* Print Actions */}
          <div className="print-actions">
            {deal.purchaseInfo && (
              <button 
                type="button" 
                onClick={handleExportPurchaseDO}
                className="print-btn"
                title="Export Purchase DO"
                disabled={exportingPurchaseDO}
              >
                {exportingPurchaseDO ? (
                  <>
                    <Loader2 size={16} className="spinning" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Export Purchase DO
                  </>
                )}
              </button>
            )}
            {deal.sellInfo && (
              <button 
                type="button" 
                onClick={handleExportSellDO}
                className="print-btn"
                title="Export Sell DO"
                disabled={exportingSellDO}
              >
                {exportingSellDO ? (
                  <>
                    <Loader2 size={16} className="spinning" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Export Sell DO
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealView; 